import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { User } from 'src/app/services/auth.service';
import { Chat, ChatCounterpart } from 'src/app/services/chat.service';
import { selectUser } from 'src/app/store/selectors/auth.selectors';
import { formatUserLabel } from 'src/app/utils/user-label';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'mvp-chats-list',
  templateUrl: './chats-list.component.html',
})
export class ChatsListComponent implements OnInit {

  @Input() chats: Chat[];
  user$: Observable<User>;

  constructor(
    private router: Router,
    private store: Store,
  ) {
    this.user$ = this.store.select(selectUser);
  }

  ngOnInit() {
    //
  }

  goToChat(chat: Chat) {
    this.router.navigate([`/chat/${chat.uuid}`]);
  }

  getCounterpart(chat: Chat, user: User) {
    if (chat?.counterpart && (!chat.counterpart.uuid || chat.counterpart.uuid !== user?.uuid)) {
      return chat.counterpart;
    }

    if (!chat?.assignee || !chat?.initiator) {
      return null;
    }

    return chat.assignee.uuid === user.uuid ? chat.initiator : chat.assignee;
  }

  isServiceCounterpart(counterpart: User | ChatCounterpart | null): counterpart is ChatCounterpart {
    return !!counterpart && !!(counterpart as ChatCounterpart).is_service;
  }

  getCounterpartLabel(counterpart: any): string {
    if (!counterpart) {
      return '';
    }

    if (counterpart.label) {
      return counterpart.label;
    }

    if (this.isServiceCounterpart(counterpart) && counterpart.type === 'cse') {
      return this.cseLabel;
    }

    if (this.isServiceCounterpart(counterpart) && counterpart.type === 'maviepro') {
      return this.supportServiceLabel;
    }

    return formatUserLabel(counterpart);
  }

  getCounterpartSubtitle(counterpart: any): string {
    if (!counterpart) {
      return '';
    }

    if (counterpart.organization) {
      return counterpart.organization;
    }

    return '';
  }

  getCounterpartType(counterpart: User | ChatCounterpart | null): string {
    if (!counterpart || !this.isServiceCounterpart(counterpart)) {
      return 'interlocutor';
    }
    return counterpart.type || 'interlocutor';
  }

  serviceIconName(counterpart: User | ChatCounterpart | null): string {
    return this.getCounterpartType(counterpart) === 'maviepro' ? 'headphones' : 'mail';
  }

  serviceAvatarClass(counterpart: User | ChatCounterpart | null): string {
    return this.getCounterpartType(counterpart) === 'maviepro' ? 'bg-[DBE3FE]' : 'bg-[#285D91]';
  }

  latestMessageContent(chat: Chat): string {
    const last = this.latestMessage(chat);
    if (!last?.content) {
      return '';
    }
    return String(last.content);
  }

  latestMessageDate(chat: Chat): string | null {
    const last = this.latestMessage(chat);
    return last?.created_at || null;
  }

  latestMessageDeliveryStatus(chat: Chat, user: User): 'sent' | 'read' | null {
    const last = this.latestMessage(chat);
    if (!last || last.is_system || last?.creator?.uuid !== user?.uuid) {
      return null;
    }

    if ((chat?.recipient_type || 'interlocutor') !== 'interlocutor') {
      return 'sent';
    }

    const messageDate = this.parseDate(last?.created_at);
    const counterpartReadDate = this.parseDate(chat?.counterpart_last_read_at);

    if (!messageDate || !counterpartReadDate) {
      return 'sent';
    }

    return counterpartReadDate.getTime() >= messageDate.getTime() ? 'read' : 'sent';
  }

  unreadCount(chat: Chat): number {
    if (Array.isArray(chat?.newMessages)) {
      return chat.newMessages.length;
    }

    if (typeof chat?.newMessagesCounter === 'number') {
      return chat.newMessagesCounter;
    }

    return 0;
  }

  hasUnread(chat: Chat): boolean {
    return this.unreadCount(chat) > 0;
  }

  private latestMessage(chat: Chat): any {
    const unreadList = Array.isArray(chat?.newMessages) ? chat.newMessages : [];
    const messagesList = Array.isArray(chat?.messages) ? chat.messages : [];
    const list = unreadList.length > 0 ? unreadList : messagesList;
    return list.length > 0 ? list[list.length - 1] : null;
  }

  private parseDate(date: string): Date | null {
    if (!date) {
      return null;
    }

    const normalized = String(date)
      .trim()
      .replace(/\s+/g, 'T')
      .replace(/\.(\d{3})\d+(?=(Z|[+-]\d{2}:?\d{2})?$)/, '.$1');

    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private get cseLabel(): string {
    return String(environment.application || '').toLowerCase().trim() === 'tract'
      ? 'Syndicat'
      : 'CSE';
  }

  private get supportServiceLabel(): string {
    return String(environment.application || '').toLowerCase().trim() === 'tract'
      ? 'Service technique Tract'
      : 'Service technique Maviepro';
  }
}
